import { useState } from 'react';
import { X } from 'lucide-react';
import { Badge, Label } from '@/ui/design-system/components';
import { cn } from '@/ui/design-system/utils/cn';
import { isValidEmail, parseReceiverEmails } from './receiverEmails';

/**
 * Tokenized email input: each address becomes a removable chip. A token is
 * validated with isValidEmail before it is accepted, so an invalid recipient
 * list can never be submitted. Value is the canonical array of email strings.
 */
function RecipientEmailsInput({
    id = 'receiver_emails',
    label = 'Recipients',
    value = [],
    onChange,
    disabled = false,
    placeholder = 'email1@example.com, email2@example.com',
}) {
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState(null);

    const commit = (raw) => {
        const tokens = parseReceiverEmails(raw);
        if (tokens.length === 0) {
            setInputValue('');
            return;
        }
        const valid = [];
        const invalid = [];
        tokens.forEach((token) => {
            if (!isValidEmail(token)) {
                invalid.push(token);
            } else if (!value.includes(token) && !valid.includes(token)) {
                valid.push(token);
            }
        });
        if (valid.length) {
            onChange?.([...value, ...valid]);
        }
        // Keep any invalid tokens in the field so the user can fix them.
        setInputValue(invalid.join(', '));
        setError(
            invalid.length ? `Not a valid email: ${invalid.join(', ')}` : null,
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit(inputValue);
        } else if (
            e.key === 'Backspace' &&
            inputValue === '' &&
            value.length > 0
        ) {
            onChange?.(value.slice(0, -1));
        }
    };

    const handleRemove = (email) => {
        onChange?.(value.filter((e) => e !== email));
    };

    return (
        <div className="flex flex-col gap-2">
            {label && <Label htmlFor={id}>{label}</Label>}
            <div
                className={cn(
                    'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border bg-[var(--background)] px-2 py-1 text-sm transition-colors',
                    'focus-within:ring-[3px] focus-within:ring-offset-0',
                    error
                        ? 'border-destructive focus-within:border-destructive focus-within:ring-[var(--ring-destructive)]'
                        : 'border-input focus-within:border-ring focus-within:ring-ring',
                    disabled && 'cursor-not-allowed opacity-50',
                )}
            >
                {value.map((email) => (
                    <Badge
                        key={email}
                        type="button"
                        variant="secondary"
                        interactive
                        disabled={disabled}
                        className="h-6 px-1.5"
                        onClick={() => !disabled && handleRemove(email)}
                    >
                        <span className="whitespace-nowrap text-xs">
                            {email}
                        </span>
                        <X className="h-3 w-3 text-muted-foreground" />
                    </Badge>
                ))}
                <input
                    id={id}
                    type="text"
                    disabled={disabled}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        if (error) setError(null);
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={() => commit(inputValue)}
                    placeholder={value.length ? '' : placeholder}
                    className="flex-1 min-w-[8rem] border-0 bg-transparent px-1 py-0.5 placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed"
                />
            </div>
            {error ? (
                <span className="text-sm text-destructive">{error}</span>
            ) : (
                <span className="text-sm text-muted-foreground">
                    Add recipients one at a time or paste a list (comma, space,
                    or semicolon separated).
                </span>
            )}
        </div>
    );
}

export default RecipientEmailsInput;
